import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/apiService';
import './Login.css'; // Reuse login styles

interface ChangePasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState<ChangePasswordForm>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [changeComplete, setChangeComplete] = useState(false);

  // Get user data from location state or localStorage
  const userData = location.state?.user || JSON.parse(localStorage.getItem('user_data') || '{}');
  const message = location.state?.message || 'Please change your temporary password';

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }

    // Check if password change is actually required
    if (!userData.require_password_change) {
      navigate('/upload', { replace: true });
    }
  }, [navigate, userData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/\d/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const customer_id = userData.customer_id || userData.email?.toUpperCase();

      await apiService.changePassword(
        customer_id,
        formData.currentPassword,
        formData.newPassword,
        formData.confirmPassword
      );

      // Update user data in localStorage
      const updatedUserData = {
        ...userData,
        require_password_change: false
      };
      localStorage.setItem('user_data', JSON.stringify(updatedUserData));

      setChangeComplete(true);

      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/upload', { replace: true });
      }, 2000);

    } catch (error: any) {
      console.error('Change password error:', error);
      if (error.response?.status === 401) {
        setError('Current password is incorrect');
      } else if (error.response?.status === 400) {
        setError(error.response?.data?.detail || 'Invalid password');
      } else {
        setError(error.response?.data?.detail || 'Failed to change password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (changeComplete) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <h1>Password Changed Successfully</h1>
            <p>Your password has been updated</p>
          </div>

          <div className="success-message" style={{
            background: '#d4edda',
            border: '1px solid #c3e6cb',
            color: '#155724',
            padding: '20px',
            borderRadius: '8px',
            textAlign: 'center',
            marginBottom: '20px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>✓</div>
            <h3>Success!</h3>
            <p>Your password has been changed successfully.</p>
            <p>Redirecting to upload page...</p>
          </div>

          <button
            onClick={() => navigate('/upload', { replace: true })}
            className="login-button"
          >
            Continue to Upload
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>Change Your Password</h1>
          <p>{message}</p>
        </div>

        {/* Info banner for temporary password */}
        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffc107',
          color: '#856404',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <strong>⚠️ Security Notice:</strong> You're currently using a temporary password.
          Please create a new secure password to continue.
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="currentPassword">Current Password (Temporary)</label>
            <div className="password-input-container">
              <input
                type={showPasswords ? 'text' : 'password'}
                id="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleInputChange}
                placeholder="Enter your current temporary password"
                required
                autoComplete="current-password"
                className={error ? 'error' : ''}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPasswords(!showPasswords)}
                aria-label={showPasswords ? 'Hide passwords' : 'Show passwords'}
              >
                {showPasswords ? '👁' : '●'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type={showPasswords ? 'text' : 'password'}
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleInputChange}
              placeholder="Enter your new password"
              required
              autoComplete="new-password"
              className={error ? 'error' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type={showPasswords ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="Confirm your new password"
              required
              autoComplete="new-password"
              className={error ? 'error' : ''}
            />
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon"><strong>⚠</strong></span>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword}
            className="login-button"
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Changing Password...
              </>
            ) : (
              'Change Password'
            )}
          </button>

          <div className="login-footer">
            <p style={{ fontSize: '14px', color: '#6c757d' }}>
              Need help? Contact support
            </p>
          </div>
        </form>

        {/* Password Requirements */}
        <div className="security-info" style={{
          background: '#f8f9fa',
          padding: '15px',
          borderRadius: '8px',
          marginTop: '20px'
        }}>
          <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>🔒 Password Requirements</h3>
          <ul style={{
            fontSize: '14px',
            lineHeight: '1.8',
            paddingLeft: '20px',
            margin: 0
          }}>
            <li>At least 8 characters long</li>
            <li>Contains at least one uppercase letter</li>
            <li>Contains at least one lowercase letter</li>
            <li>Contains at least one number</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
